<script setup lang="ts">
import startCase from 'lodash/startCase';

import { normalizeToDisplayLabel } from '@/utils/previousValue';

import Badge from '@/components/badge/Badge.vue';
import PaddedCellContent from './PaddedCellContent.vue';

type AttributeMap = Record<string, unknown> | null | undefined;

const props = defineProps<{
  attributes: AttributeMap;
}>();

function computeFirstNonNullAttribute(attributeMap: AttributeMap): {
  key: string;
  display: string;
  remaining: number;
} {
  const attributeEntries =
    attributeMap && typeof attributeMap === 'object' ? Object.entries(attributeMap) : [];

  const getValue = (value: unknown): string | null => {
    const display = normalizeToDisplayLabel(value);
    if (display === undefined) {
      return null;
    }
    return String(display);
  };

  const attributeKeys = new Set(attributeEntries.map(([attributeKey]) => attributeKey));
  const priorityKeys: string[] = [];
  ['scholarship', 'institute', 'type', 'status'].forEach((key) => {
    if (attributeKeys.has(key)) {
      priorityKeys.push(key);
    }
  });

  const prioritized = priorityKeys
    .map((priorityKey) => ({
      key: priorityKey,
      value: (attributeMap as Record<string, unknown> | undefined)?.[priorityKey]
    }))
    .map(({ key, value }) => ({ key, display: getValue(value) ?? '' }))
    .find((candidate) => candidate.display !== '');

  const fallback = attributeEntries
    .map(([attributeKey, attributeValue], index) => ({
      key: attributeKey,
      display: getValue(attributeValue) ?? '',
      index,
      value: attributeValue
    }))
    .find((candidate) => candidate.display !== '');

  const selectedKey = prioritized?.key ?? fallback?.key ?? '';
  const selectedDisplay = prioritized?.display ?? fallback?.display ?? '';
  const selectedIndex = selectedKey
    ? attributeEntries.findIndex(([attributeKey]) => attributeKey === selectedKey)
    : -1;

  if (!selectedKey || selectedDisplay === '') {
    return { key: '', display: '', remaining: 0 };
  }

  const remaining = attributeEntries.filter(
    ([, value], index) => index !== selectedIndex && value != null
  ).length;

  return { key: selectedKey, display: selectedDisplay, remaining };
}

const result = computed(() => computeFirstNonNullAttribute(props.attributes));
const primaryText = computed(() => startCase(result.value.key));

const badges = computed(() => {
  const items: string[] = [];
  if (result.value.display) {
    items.push(result.value.display);
  }
  if (result.value.remaining > 0) {
    items.push(`+${result.value.remaining} more`);
  }
  return items;
});
</script>

<template>
  <PaddedCellContent>
    <div class="flex flex-col text-sm">
      <span class="font-medium leading-5 text-slate-700">{{ primaryText }}</span>
      <div class="mt-1 flex flex-wrap gap-1">
        <Badge
          v-for="label in badges"
          :key="label"
          :label="label"
          shape="rounded"
          variant="outlined"
          class="truncate"
        />
      </div>
    </div>
  </PaddedCellContent>
</template>
